import React from "react";
import CKEditor from '@ckeditor/ckeditor5-react'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

import "./RichTextEditorField.css";

const EMPTY_EDITOR_VALUES = ["", "<p>&nbsp;</p>", "<p><br></p>"];

/**
 * Resolve the initial editor value from all possible sources.
 * Priority: explicit value → defaultValue (from answer_data) → data.value (from item JSON)
 */
const getInitialEditorValue = (props) => {
  return (
    props?.value ||
    props?.defaultValue ||
    props?.mutable?.value ||
    props?.answer ||
    props?.data?.value ||
    ""
  );
};

const RichTextEditorField = React.forwardRef((props, forwardedRef) => {
  // CustomElement passes field data via props.data; direct props may be absent
  const data = props.data || {};
  const label = props.label || data.label;
  const required = props.required ?? data.required;
  const read_only = props.read_only || props.disabled;
  const placeholder = props.placeholder || data.props?.placeholder;

  // field_name from props.name (set by CustomElement) or data.field_name
  const inputName = props.name || props.field_name || data.field_name || "rich_text_editor";

  const hiddenInputRef = React.useRef(null);
  const editorInstanceRef = React.useRef(null);
  // Track the last value we programmatically pushed into the editor so we
  // don't fight with user typing.
  const lastExternalValueRef = React.useRef(null);
  // Track whether the user is actively editing (onChange fires) so we skip
  // external syncs that would overwrite their work.
  const isUserEditingRef = React.useRef(false);

  /**
   * Merge the forwarded ref (from CustomElement.inputField) with our local ref
   * so that _getItemValue can read hiddenInput.value via ref.inputField.current.
   */
  const setHiddenInputRef = React.useCallback(
    (node) => {
      hiddenInputRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef]
  );

  // Compute the initial value only once for CKEditor's `data` prop.
  const [initialData] = React.useState(() => getInitialEditorValue(props));

  // Current value kept in state purely for the hidden input & validation display.
  const [currentValue, setCurrentValue] = React.useState(() => initialData);

  /**
   * Sync from parent when props change (e.g. navigating back with answer_data).
   * Only apply if the incoming value genuinely differs from what we last set
   * externally AND the user is not currently typing.
   */
  React.useEffect(() => {
    const incoming = getInitialEditorValue(props);
    // Allow empty string as a valid incoming value (don't skip it).
    if (
      incoming !== undefined &&
      incoming !== null &&
      incoming !== lastExternalValueRef.current &&
      !isUserEditingRef.current
    ) {
      lastExternalValueRef.current = incoming;
      setCurrentValue(incoming);
      // Push into the live editor instance if available
      if (editorInstanceRef.current) {
        try {
          const editorCurrentData = editorInstanceRef.current.getData();
          if (editorCurrentData !== incoming) {
            editorInstanceRef.current.setData(incoming);
          }
        } catch (_) {
          // editor may be destroyed during unmount – ignore
        }
      }
      // Also keep item.value in sync for _collect fallback
      if (data) {
        data.value = incoming;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.defaultValue, props.mutable?.value, props.answer, props.data?.value]);

  const updateFormBuilderValue = (newValue) => {
    setCurrentValue(newValue);
    lastExternalValueRef.current = newValue;

    /**
     * CRITICAL: Store the value directly on the item data object.
     * This is the most reliable persistence path because _collect in form.jsx
     * can read item.value as a fallback even when the ref chain is unavailable
     * (e.g. templates saved before forwardRef was added).
     */
    if (data) {
      data.value = newValue;
    }

    // Keep the hidden input DOM value in sync for ref-based collection
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = newValue;

      // Dispatch a native input event so the <form> onChange handler fires.
      const nativeEvent = new Event("input", { bubbles: true });
      hiddenInputRef.current.dispatchEvent(nativeEvent);
    }

    // Optional callbacks
    if (typeof props.onChange === "function") {
      props.onChange(newValue);
    }
    if (typeof props.handleChange === "function") {
      props.handleChange(inputName, newValue);
    }
  };

  const handleEditorChange = (_event, editor) => {
    isUserEditingRef.current = true;
    const val = editor.getData();
    updateFormBuilderValue(val);
    // Reset the flag after a micro-tick so the useEffect won't collide
    // with this change cycle.
    Promise.resolve().then(() => {
      isUserEditingRef.current = false;
    });
  };

  const isInvalid =
    required && EMPTY_EDITOR_VALUES.includes(String(currentValue || "").trim());

  return (
    <div className="rich-text-editor-wrapper">
      {label && (
        <label className="rich-text-editor-label" htmlFor={inputName}>
          {label}
          {required && <span className="rich-text-editor-required"> *</span>}
        </label>
      )}

      <CKEditor
        editor={ClassicEditor}
        data={initialData || ""}
        disabled={Boolean(read_only)}
        config={{
          placeholder: placeholder || "Enter rich text content...",
          toolbar: [
            "heading",
            "|",
            "bold",
            "italic",
            "link",
            "bulletedList",
            "numberedList",
            "|",
            "outdent",
            "indent",
            "|",
            "blockQuote",
            "insertTable",
            "|",
            "undo",
            "redo",
          ],
        }}
        onReady={(editor) => {
          editorInstanceRef.current = editor;
          // If an answer_data value was resolved after CKEditor mounted with
          // empty initial data, push it now.
          const latestValue = getInitialEditorValue(props);
          if (latestValue && latestValue !== (initialData || "")) {
            editor.setData(latestValue);
            lastExternalValueRef.current = latestValue;
            setCurrentValue(latestValue);
            if (data) {
              data.value = latestValue;
            }
          } else {
            lastExternalValueRef.current = initialData || "";
          }
        }}
        onChange={handleEditorChange}
      />

      <input
        ref={setHiddenInputRef}
        type="hidden"
        id={inputName}
        name={inputName}
        value={currentValue || ""}
        required={required}
        onChange={() => {}}
        readOnly
      />

      {isInvalid && (
        <div className="rich-text-editor-error">
          This field is required.
        </div>
      )}
    </div>
  );
});

RichTextEditorField.displayName = "RichTextEditorField";

export default RichTextEditorField;